<template>
  <div class="text-center">
    <v-stepper
      v-model="currentStep"
      flat
      class="status-stepper"
      vertical
      hide-actions
      :items="
        statuses.map(status => {
          return {
            value: status.stepNumber,
            title: status.name
          };
        })
      "
      :class="{ 'mb-8': !dense, compact }">
      <v-stepper-window>
        <v-stepper-window-item
          :value="status.stepNumber"
          v-for="(status, index) in statuses"
          :key="`status-${index}`">
          <div>
            <v-container>
              <v-row v-if="$rolePermissions.canUpdateAppointmentStatus">
                <!-- Next Happy Path Button -->
                <v-btn
                  variant="text"
                  block
                  @click="updateStatus(getNextHappyPathStatus(status))"
                  v-if="shouldShowNextHPButton(status)"
                  class="justify-start align-center no-text-transform">
                  <v-icon
                    size="small"
                    :color="getNextHappyPathStatus(status).toLowerCase()"
                    class="mr-2">
                    mdi-circle
                  </v-icon>
                  {{
                    novaCore.capitalize(getButtonLabel(statusMap[getNextHappyPathStatus(status)]))
                  }}
                </v-btn>

                <!-- Undo Button -->
                <v-btn
                  variant="text"
                  block
                  v-if="status.canUndo"
                  class="justify-start align-center no-text-transform"
                  @click="undoLatestStatus">
                  <v-icon size="small" class="mr-2">mdi-undo</v-icon>
                  {{ `Undo ${novaCore.breakWordsAtCaps(status.name)} Status` }}
                </v-btn>

                <!-- Next Non-Happy Path Button -->
                <v-btn
                  variant="text"
                  block
                  class="justify-start align-center no-text-transform"
                  v-if="getNextNonHappyPathStatus(status) && !status.isEndState"
                  @click="updateStatus(getNextNonHappyPathStatus(status))">
                  <v-icon
                    v-if="getStatusIcon(statusMap[getNextNonHappyPathStatus(status)])"
                    size="small"
                    class="mr-2">
                    {{ getStatusIcon(statusMap[getNextNonHappyPathStatus(status)]) }}
                  </v-icon>
                  <v-icon
                    v-else
                    size="small"
                    :color="getNextNonHappyPathStatus(status).toLowerCase()"
                    class="mr-2">
                    mdi-circle
                  </v-icon>
                  {{ getButtonLabel(statusMap[getNextNonHappyPathStatus(status)]) }}
                </v-btn>
              </v-row>
            </v-container>
          </div>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>

    <status-timeline-edit-dialog
      :external-activator="true"
      :status-to-edit="statusToEdit"
      :mixpanel-entry-point="mixpanelEntryPoint"
      :show-dialog="showEditDialog"
      :appointment="appointment"
      @close="closeEditor"></status-timeline-edit-dialog>

    <custom-forms-flow-dialog-old
      :external-activator="true"
      :warehouse="warehouse"
      :show-dialog="hasActiveTrigger"
      :object-id="appointment.id"
      :trigger="activeTrigger"
      :status-change="pendingStatusChange"
      @close="cleanUpCustomFormsData"
      @update="updateCustomFormsData"
      @create="submitStatusWithCustomFormsData"></custom-forms-flow-dialog-old>
  </div>
</template>

<script>
import etaMixin from '@satellite/components/mixins/etaMixin';
import statusManagerMixin from '@/components/mixins/statusManagerMixin';
import { AssetVisitEventType } from '@nova/core';

const BTN_HAPPY = 'BTN_HAPPY';
const BTN_NON_HAPPY = 'BTN_NON_HAPPY';
const BTN_UNDO = 'BTN_UNDO';

/**
 * @displayName Status Manager
 */
export default {
  name: 'StatusManager',
  mixins: [etaMixin, statusManagerMixin],
  props: {
    mixpanelEntryPoint: {
      type: String,
      required: false
    },
    warehouseTriggers: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data() {
    return {
      lastBtnClicked: null
    };
  },
  computed: {
    btnHappy_loading: function () {
      return this.loading && this.lastBtnClicked === BTN_HAPPY;
    },
    btnNonHappy_loading: function () {
      return this.loading && this.lastBtnClicked === BTN_NON_HAPPY;
    },
    btnUndo_loading: function () {
      return this.loading && this.lastBtnClicked === BTN_UNDO;
    },
    warehouse() {
      // Used by statusManagerMixin
      return this.appointment?.dock?.warehouse;
    },
    isUnplannedAppointment() {
      return this.appointment?.assetVisit?.isPlanned === false;
    },
    isCarrierCheckin() {
      return this.appointment?.isCheckedInByCarrier;
    },
    isAssetDeparted() {
      return this.appointment?.assetVisit?.assetVisitEvents.some(
        event => event.eventType === AssetVisitEventType.Departed
      );
    },
    hasCheckinStatus() {
      return this.isCarrierCheckin || this.isUnplannedAppointment || this.isAssetDeparted;
    }
  },
  methods: {
    btnHappy_onClick(status) {
      if (this.loading) {
        return;
      }
      this.updateStatus(this.getNextHappyPathStatus(status));
      this.lastBtnClicked = BTN_HAPPY;
    },
    btnNonHappy_onClick(status) {
      if (this.loading) {
        return;
      }
      this.updateStatus(this.getNextNonHappyPathStatus(status));
      this.lastBtnClicked = BTN_NON_HAPPY;
    },
    btnUndo_onClick() {
      if (this.loading) {
        return;
      }
      this.undoLatestStatus();
      this.lastBtnClicked = BTN_UNDO;
    }
  }
};
</script>

<style scoped lang="scss">
:deep(.v-stepper-header) {
  display: none;
}

:deep(.v-stepper-header .v-window:first-child) {
  display: none;
}
:deep(.v-stepper-window) {
  margin: 0;
}
</style>
